import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from '../../hooks/use-auth';
import { inactivityManager } from '../../lib/inactivity-manager';
import { AutoLogoutDialog } from '../ui/auto-logout-dialog';

interface InactivityContextType {
  isWarningActive: boolean;
  extendSession: () => void;
  forceLogout: () => void;
}

const InactivityContext = createContext<InactivityContextType | null>(null);

interface InactivityProviderProps {
  children: ReactNode;
}

export function InactivityProvider({ children }: InactivityProviderProps) {
  const [isWarningActive, setIsWarningActive] = useState(false);
  const [warningTime, setWarningTime] = useState(0);
  const { user, logoutMutation } = useAuth();
  
  // Initialize inactivity manager when user is authenticated
  useEffect(() => {
    if (!user) {
      // Clean up if user logs out
      inactivityManager.destroy();
      return;
    }
    
    // Only initialize if not already initialized
    if (!inactivityManager['_initialized']) {
      console.log('🔒 Initializing inactivity manager for authenticated user');
      
      const handleWarning = () => {
        console.log('⚠️ Showing inactivity warning');
        setWarningTime(inactivityManager.getWarningTime());
        setIsWarningActive(true);
      };
      
      const handleLogout = () => {
        console.log('🔒 Auto-logout triggered');
        setIsWarningActive(false);
        logoutMutation.mutate();
      };
      
      inactivityManager.initialize(handleWarning, handleLogout);
      inactivityManager['_initialized'] = true;
    }
    
    return () => {
      // Only destroy on unmount or when user changes
      if (!user) {
        inactivityManager.destroy();
        inactivityManager['_initialized'] = false;
      }
    };
  }, [user]); // Only depend on user, not isWarningActive or logoutMutation
  
  const extendSession = () => {
    console.log('✅ Session extended by user');
    setIsWarningActive(false);
    setWarningTime(0);
  };
  
  const forceLogout = () => {
    console.log('🔒 Force logout confirmed');
    setIsWarningActive(false);
    setWarningTime(0);
  };
  
  const contextValue: InactivityContextType = {
    isWarningActive,
    extendSession,
    forceLogout,
  };
  
  return (
    <InactivityContext.Provider value={contextValue}>
      {children}
      {user && (
        <AutoLogoutDialog
          isOpen={isWarningActive}
          onExtend={extendSession}
          onLogout={forceLogout}
          warningTime={warningTime}
        />
      )}
    </InactivityContext.Provider>
  );
}

export function useInactivity() {
  const context = useContext(InactivityContext);
  if (!context) {
    throw new Error('useInactivity must be used within an InactivityProvider');
  }
  return context;
}
